'use client';

import useUpdateEffect from '@/hooks/useUpdateEffect';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Pagination as NextUIPagination,
  PaginationProps,
} from "@heroui/pagination";
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React from 'react';
import clsx from 'clsx';

interface CustomPaginationProps extends PaginationProps {
  isQuery?: boolean;
  queryName?: string;
  isScroll?: boolean;
}

const Pagination: React.FC<CustomPaginationProps> = ({
  isQuery = false,
  isScroll = true,
  queryName = 'page',
  ...props
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [pageState, setPageState] = React.useState(
    parseInt(searchParams.get(queryName) as string) || props.initialPage || 1
  );

  useUpdateEffect(() => {
    if (!searchParams.get(queryName)) {
      setPageState(props.initialPage || 1);
      return;
    }
    setPageState(parseInt(searchParams.get(queryName) as string));
  }, [searchParams]);

  const changeUrl = (page: number) => {
    const queryParams = new URLSearchParams(searchParams.toString());
    queryParams.set(queryName, page.toString());
    router.push(`${pathname}?${queryParams.toString()}`, {
      scroll: isScroll,
    });
  };

  const handleChange = (page: number) => {
    props.onChange?.(page);
    setPageState(page);

    if (isQuery) {
      changeUrl(page);
    }
  };

  return (
    <div className="flex justify-center items-center">
      <FontAwesomeIcon
        icon={['fas', 'angles-left']}
        className={clsx(
          'text-[12px] p-3 rounded-extra duration-300',
          pageState === 1
            ? 'text-[#D4D4D8] cursor-default'
            : 'text-gray-medium cursor-pointer hover:bg-gray-lightest hover:text-gray-dark focus:!bg-primary-extra-light-blue'
        )}
        onClick={() => pageState !== 1 && handleChange(1)}
      />
      <NextUIPagination
        variant="light"
        classNames={{
          cursor: '!bg-primary-extra-light-blue !rounded-extra !text-gray-dark',
          item: '!rounded-extra',
          next: '!rounded-extra',
          prev: '!rounded-extra',
        }}
        {...props}
        page={pageState}
        onChange={handleChange}
      />
      <FontAwesomeIcon
        icon={['fas', 'angles-right']}
        className={clsx(
          'text-[12px] p-3 rounded-extra duration-300',
          pageState === props.total
            ? 'text-[#D4D4D8] cursor-default'
            : 'text-gray-medium cursor-pointer hover:bg-gray-lightest hover:text-gray-dark focus:!bg-primary-extra-light-blue'
        )}
        onClick={() => pageState !== props.total && handleChange(props.total)}
      />
    </div>
  );
};

export default Pagination;
